import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Check, Crown, Search, Layers, Wand2, Camera, Hash, MessageSquare, Compass, Megaphone, FileText, Image as ImageIcon, Brain, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import Footer from "@/components/app/Footer";
import { BRAND } from "@/lib/brand";

const ABLEFY_LIFETIME = BRAND.ablefy.lifetime;

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative overflow-hidden text-white">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] blur-[140px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.10) 0%, rgba(200,255,77,0.06) 40%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-0 w-[600px] h-[300px] blur-[100px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(200,255,77,0.08) 0%, transparent 70%)' }} />

      <div className="relative z-10">
        {/* Top bar */}
        <header className="px-4 sm:px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ background: `linear-gradient(135deg, ${BRAND.colors.cyan} 0%, ${BRAND.colors.lime} 100%)` }}>
              <Brain className="w-4 h-4 text-black" />
            </div>
            <span className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{BRAND.name}</span>
          </div>
          <button onClick={() => navigate("/auth")} className="text-xs text-zinc-300 hover:text-white px-4 py-2 rounded-lg border border-zinc-800 hover:border-zinc-600 transition-colors" data-testid="header-login-btn">
            Einloggen
          </button>
        </header>

        {/* HERO */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6"
               style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)' }}>
            <Sparkles className="w-3 h-3" style={{ color: BRAND.colors.cyan }} />
            <span className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: BRAND.colors.cyan }}>
              {BRAND.tagline}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Karussell-Posts<br />
            <span style={{ background: `linear-gradient(135deg, ${BRAND.colors.cyan} 0%, ${BRAND.colors.lime} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              auf Knopfdruck.
            </span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Lade <span className="text-white font-semibold">1 Selfie</span> hoch, gib dein Thema ein — die KI liefert dir <span className="text-white font-semibold">3-10 fertige Slides</span> mit echtem Storytelling, deinen Bildern, Caption und Hashtags. In 30 Sekunden.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a href={ABLEFY_LIFETIME} target="_blank" rel="noopener noreferrer"
               className="px-6 py-3 rounded-xl text-black text-sm font-bold transition-all hover:scale-105 inline-flex items-center gap-2"
               style={{ background: `linear-gradient(135deg, ${BRAND.colors.cyan} 0%, ${BRAND.colors.lime} 100%)` }}
               data-testid="hero-cta-lifetime">
              Jetzt Lifetime sichern · 97 € einmalig <ArrowRight className="w-4 h-4" />
            </a>
            <button onClick={() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-sm text-zinc-300 hover:text-white px-5 py-3" data-testid="hero-how-link">
              Wie es funktioniert ↓
            </button>
          </div>
        </section>

        {/* Loom Walkthrough Video */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 -mt-2 mb-14" data-testid="loom-video-section">
          <div className="rounded-2xl overflow-hidden border border-zinc-800 shadow-2xl shadow-cyan-500/10"
               style={{ background: '#0a0a0f' }}>
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src="https://www.loom.com/embed/b9bb9396394a46bc84a544ea91845101?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Social IQ — kurzer Walkthrough"
                className="absolute inset-0 w-full h-full"
                data-testid="loom-iframe"
              />
            </div>
          </div>
          <p className="text-center text-xs text-zinc-500 mt-3">
            ▶ In 90 Sekunden siehst du, wie Social IQ dir hilft.
          </p>
        </section>

        {/* Examples Carousel — real outputs from the generator */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-16" data-testid="examples-section">
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold mb-2"
                  style={{ background: 'rgba(200,255,77,0.1)', color: '#C8FF4D', border: '1px solid rgba(200,255,77,0.3)' }}>
              Echte Ergebnisse
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              So sehen <span style={{ color: '#00D4FF' }}>deine Karussells</span> aus
            </h2>
            <p className="text-sm text-zinc-400 mt-2">
              Editorial-Stil · ultrarealistische Fotos · perfekte Typografie
            </p>
          </div>

          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 sm:mx-0 sm:px-0"
               style={{ scrollbarWidth: 'thin' }}
               data-testid="examples-carousel">
            {[
              "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts/osxff9xh_ce292173-288b-49cc-9e82-d864be42e6ab.JPG",
              "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts/g2yca397_43c54383-5155-4e68-847c-89b4b73b1ffe.JPG",
              "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts/cqh3h2h7_b2f554db-b303-4133-b4d1-d229908d50ba.JPG",
              "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts/ezcztecm_Download%20%283%29.png",
              "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts/k2w480pj_Download%20%285%29.png",
              "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts/1cyvo63o_Download%20%286%29.png",
              "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts/269t7dsi_Download%20%287%29.png",
            ].map((src, i) => (
              <div key={i}
                   className="snap-center shrink-0 w-[78vw] sm:w-[320px] rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900 shadow-xl"
                   data-testid={`example-slide-${i + 1}`}>
                <img src={src} alt={`Beispiel-Slide ${i + 1}`}
                     loading="lazy" className="w-full h-auto block" />
              </div>
            ))}
          </div>
          <p className="text-center text-[11px] text-zinc-500 mt-1 sm:hidden">← wisch zum nächsten Beispiel →</p>
        </section>

        {/* Bonus: Free Profile Audit */}
        <section className="max-w-2xl mx-auto px-4 sm:px-6 mb-16" data-testid="free-profile-section">
          <div className="rounded-2xl p-6 sm:p-7 text-center"
               style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.25)' }}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold mb-3"
                  style={{ background: 'rgba(200,255,77,0.12)', color: BRAND.colors.lime, border: '1px solid rgba(200,255,77,0.3)' }}>
              <Search className="w-3 h-3" /> Bonus · Kostenlos
            </span>
            <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Kostenlose Profil-Analyse
            </h3>
            <p className="text-sm text-zinc-400 mb-5 max-w-md mx-auto">
              Lass dein Instagram- oder TikTok-Profil von der KI analysieren — Ghost-Follower, Engagement, Bio, Algorithmus & mehr. <span className="font-medium" style={{ color: BRAND.colors.lime }}>Eine Analyse gratis</span>.
            </p>
            <Button onClick={() => navigate("/analyse")}
                    className="text-black font-semibold"
                    style={{ background: `linear-gradient(135deg, ${BRAND.colors.cyan} 0%, ${BRAND.colors.lime} 100%)` }}
                    data-testid="free-analyse-btn">
              Profil-Analyse starten <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how" className="max-w-5xl mx-auto px-4 sm:px-6 mb-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-center mb-2" style={{ color: BRAND.colors.cyan }}>So funktioniert's</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>3 Schritte zum fertigen Karussell</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { step: "01", icon: Camera, title: "Selfie hochladen", desc: "Ein klares Foto von dir — wird als Anker für alle Slides genutzt. Einmal hochladen reicht." },
              { step: "02", icon: Wand2, title: "Thema + Stil wählen", desc: "Worüber soll der Post gehen? Wähle Storytelling-Stil, Slide-Anzahl und Format (1:1 oder Story)." },
              { step: "03", icon: Layers, title: "Fertige Slides", desc: "KI generiert deine Bilder + schreibt Hooks, Caption & Hashtags. Download und posten." },
            ].map((item, i) => (
              <div key={i} className="relative p-6 rounded-2xl bg-zinc-900/40 border border-zinc-800">
                <span className="text-5xl font-black absolute top-3 right-4" style={{ fontFamily: 'Outfit', color: 'rgba(0,212,255,0.10)' }}>{item.step}</span>
                <item.icon className="w-6 h-6 mb-3" style={{ color: BRAND.colors.cyan }} />
                <p className="text-base font-bold mb-1.5">{item.title}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* TOOLS */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-center mb-2" style={{ color: BRAND.colors.lime }}>Alles in einer Plattform</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>9 Tools für dein Social-Media-Business</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { icon: Layers, label: "Karussell-Generator", desc: "Fertige Slides in 30s." },
              { icon: Brain, label: "Brand DNA Chat", desc: "Marken-Identität in 10 Schritten." },
              { icon: Search, label: "Profil-Audit", desc: "16-Punkte Audit deines Profils." },
              { icon: Hash, label: "Hashtag-Check", desc: "Volumen & Top-Posts." },
              { icon: ImageIcon, label: "Post / Reel Audit", desc: "Hook & Caption optimieren." },
              { icon: MessageSquare, label: "Comment Goldmine", desc: "Audience-Pains aus Kommentaren." },
              { icon: Compass, label: "Virale Posts", desc: "Top-Posts deiner Nische." },
              { icon: Megaphone, label: "FB Ads Spy", desc: "Konkurrenz-Anzeigen lesen." },
              { icon: FileText, label: "Story-Check", desc: "Story-Screenshot bewerten lassen." },
            ].map((t, i) => (
              <div key={i} className="p-4 rounded-xl bg-zinc-900/40 border border-zinc-800 hover:border-cyan-500/30 transition-colors">
                <t.icon className="w-5 h-5 mb-2" style={{ color: BRAND.colors.cyan }} />
                <p className="text-sm font-semibold mb-0.5">{t.label}</p>
                <p className="text-xs text-zinc-500 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PRICING — Single Lifetime Plan */}
        <section id="pricing" className="max-w-2xl mx-auto px-4 sm:px-6 mb-20" data-testid="pricing-section">
          <p className="text-xs font-semibold uppercase tracking-widest text-center mb-2" style={{ color: BRAND.colors.cyan }}>Pricing</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Einmal zahlen. Für immer nutzen.</h2>
          <p className="text-sm text-center text-zinc-400 mb-2">Keine Abo-Falle. Kein Monatsbeitrag. Lifetime-Zugang.</p>
          <p className="text-xs text-center mb-8" style={{ color: BRAND.colors.lime }}>
            ⚡ Nur bis <strong>01.06.</strong> für 97 € — danach <span className="line-through text-zinc-500">97 €</span> <strong>149 €</strong>.
          </p>

          <div className="p-7 rounded-3xl relative" style={{ background: 'rgba(0,212,255,0.05)', border: `2px solid ${BRAND.colors.cyan}50` }} data-testid="plan-lifetime">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-black"
                 style={{ background: BRAND.colors.lime }}>
              Lifetime · Best Value
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5" style={{ color: BRAND.colors.lime }} />
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Social IQ Lifetime</h3>
            </div>
            <div className="mb-2">
              <span className="text-5xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>97</span>
              <span className="text-base text-zinc-400 ml-2">€ einmalig</span>
              <span className="ml-3 text-base text-zinc-500 line-through">149 €</span>
            </div>
            <p className="text-xs mb-2" style={{ color: BRAND.colors.lime }}>
              ⚡ Special-Launch-Preis · nur gültig bis <strong>01.06.</strong>
            </p>
            <p className="text-[11px] text-zinc-500 mb-5 leading-relaxed">
              Ab dem 01.06. steigt der Preis auf <strong className="text-zinc-300">149 €</strong> — wegen umfangreicher kommender Updates. Wer jetzt kauft, bekommt alle künftigen Features ohne Aufpreis dazu.
            </p>
            <ul className="space-y-1.5 mb-6 text-sm">
              {[
                "Unlimitierte Karussell-Generierung",
                "Brand DNA Chat (GPT-5.2)",
                "Profil-Audit · Hashtag-Check · Post-Audit",
                "Comment Goldmine · Trends · Facebook-Tools",
                "Story-Check (Vision-KI)",
                "Premium-Affiliate · 45 % Provision",
                "Alle zukünftigen Features inkludiert",
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-zinc-300">
                  <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND.colors.cyan }} />{f}
                </li>
              ))}
            </ul>
            <a href={ABLEFY_LIFETIME} target="_blank" rel="noopener noreferrer" data-testid="buy-lifetime-plan">
              <Button className="w-full text-black font-bold h-12 text-base"
                      style={{ background: `linear-gradient(135deg, ${BRAND.colors.cyan} 0%, ${BRAND.colors.lime} 100%)` }}>
                Jetzt Lifetime kaufen — 97 €
              </Button>
            </a>
          </div>

          <div className="text-center pt-8">
            <p className="text-sm text-zinc-500">
              Bereits gekauft?{" "}
              <button onClick={() => navigate("/auth")} className="hover:underline transition-colors" style={{ color: BRAND.colors.cyan }} data-testid="already-bought-link">
                Account aktivieren
              </button>
            </p>
          </div>
        </section>

        <Footer variant="public" />
      </div>
    </div>
  );
}
